/**
 * The Test tab's state: the email to try and as whom, earlier messages when it continues a thread,
 * whether drafts compete, the result, and the AI draft (asked for separately: it takes up to half a
 * minute). Nothing is sent or recorded by trying.
 */
import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { wizardMagicEmailApi, type EmailTestRequest } from '../api/api';
import { errorText } from './use-magic-email-library';

export interface TestTurn {
  role: 'advisor' | 'assistant';
  content: string;
}

export function useEmailTest() {
  const [advisorEmail, setAdvisorEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [email, setEmail] = useState('');
  const [includeDrafts, setIncludeDrafts] = useState(false);
  const [history, setHistory] = useState<TestTurn[]>([]);

  const run = useMutation({
    mutationFn: wizardMagicEmailApi.testEmail,
    onError: error => toast.error(`Could not try it: ${errorText(error)}`),
  });
  const draft = useMutation({
    mutationFn: wizardMagicEmailApi.testEmailDraft,
    onError: error => toast.error(`No AI draft: ${errorText(error)}`),
  });

  const request = useCallback(
    (): Omit<EmailTestRequest, 'includeDrafts'> => ({
      email,
      ...(advisorEmail.trim() ? { advisorEmail: advisorEmail.trim() } : {}),
      ...(subject.trim() ? { subject: subject.trim() } : {}),
      history: history.filter(turn => turn.content.trim()),
    }),
    [advisorEmail, email, history, subject],
  );

  const tryIt = useCallback(() => {
    draft.reset();
    run.mutate({ ...request(), includeDrafts });
  }, [draft, includeDrafts, request, run]);

  /** The same email again, with one reply competing as its newest draft. */
  const tryAgainWith = useCallback(
    (draftOf: string) => {
      draft.reset();
      run.mutate({ ...request(), includeDrafts, draftOf });
    },
    [draft, includeDrafts, request, run],
  );

  return {
    form: { advisorEmail, subject, email, includeDrafts },
    setAdvisorEmail,
    setSubject,
    setEmail,
    setIncludeDrafts,
    history,
    addTurn: (role: TestTurn['role']) => setHistory(turns => [...turns, { role, content: '' }]),
    setTurn: (index: number, content: string) =>
      setHistory(turns => turns.map((turn, i) => (i === index ? { ...turn, content } : turn))),
    removeTurn: (index: number) => setHistory(turns => turns.filter((_, i) => i !== index)),
    canTry: email.trim().length > 0 && !run.isPending,
    tryIt,
    tryAgainWith,
    trying: run.isPending,
    result: run.data ?? null,
    /** Changes with every run, so per-result state can start afresh. */
    runId: run.submittedAt,
    /** The reply that competed as its draft in this run, after an example was added to it. */
    draftOf: run.variables?.draftOf ?? null,
    writeDraft: () => draft.mutate(request()),
    draft,
  };
}
